<template>
  <div>
    <NotificationMessage class="notification" />
    <div class="layout" v-if="room">
      <div class="mb-5 parent">
        <div class="div1">
          <details-room
            :room="room"
            @redirectToPrevious="redirectToCenters(room)"
            @rediretToPreviousPage="returnToRoomList(room)"
            @subscribe="subscription(room)"
            :centerTooltip="centerTooltip"
            :roomTooltip="roomTooltip"
            :availableDesks="availableDesks"
            :bookInterval="bookInterval"
            :bookedDesks="bookedDesks"
            :desksAvailable="getDesksAvailable"
          />
        </div>
        <div class="div2">
          <div class="neumorphism-out list">
            <div class="list-title space-between">
              <h2 class="mr-2">
                {{ $t("crudDesks.title") }}
              </h2>
              <div>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <EditDeskDialogButton
                      :room="room"
                      :desk="newDesk"
                      @closed="clearNewDesk"
                      @saved="resizeDesk"
                      icon
                      small
                      v-on="on"
                      aria-label="add-desk-button"
                    >
                      <template #dialog-button-text>
                        <v-icon> mdi-plus-circle </v-icon>
                      </template>
                    </EditDeskDialogButton>
                  </template>
                  <span>{{ $t("roomsList.tooltipAdd") }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-btn
                      icon
                      small
                      @click="saveDesks"
                      v-on="on"
                      aria-label="save-desks-button"
                    >
                      <v-icon>mdi-content-save</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("desksList.tooltipSaveAll") }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-btn
                      icon
                      small
                      @click="resizeDesks()"
                      v-on="on"
                      aria-label="resize-desk-button"
                    >
                      <v-icon>mdi-resize</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("desksList.tooltipResizeAll") }}</span>
                </v-tooltip>
              </div>
            </div>
            <h3>
              {{ $t("crudDesks.desk") }}
            </h3>
            <DeskList
              class="desks-list"
              :room="room"
              :desks="desks"
              :planRef="$refs.desksPlan"
              :deskTooltip="deskTooltip"
              :allDesks="allDesks"
              :editMode="true"
              :availableDesks="availableDesks"
              :bookInterval="bookInterval"
              :bookedDesks="bookedDesks"
              :getDesksAvailable="getDesksAvailable"
            />
            <h3>{{ $t("crudDesks.equipment") }}</h3>
            <DeskList
              class="desks-list"
              :room="room"
              :desks="desks"
              :planRef="$refs.desksPlan"
              :deskTooltip="deskTooltip"
              :allDesks="allEquipments"
              :editMode="true"
            />
          </div>
        </div>
        <div class="div3">
          <div class="plan-desks">
            <PlanCanvas
              class="ml-3"
              ref="desksPlan"
              :shapes="desks"
              :config="planConfig"
              :editMode="true"
              canvasWidth="1200"
              canvasHeight="550"
            />
          </div>
          <div class="btn-position">
            <v-btn color="red darken-1" dark @click="changeStyle">
              {{ $t("crudCommon.changePlanStyle") }}
            </v-btn>
          </div>

          <div class="btn-position">
            <EditRoomDialogButton
              :room="roomCopy"
              @click="copyRoom"
              color="red darken-1"
              dark
            >
              <template #dialog-button-text>
                {{ $t("crudRooms.updateRoom") }}
              </template>
            </EditRoomDialogButton>
            <v-btn
              class="ml-3"
              color="red darken-1"
              dark
              @click="deleteItem(room)"
              :aria-label="$t('crudRooms.deleteRoom')"
              >{{ $t("crudRooms.deleteRoom") }}
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NotificationMessage from "@/components/components-base/notification/NotificationMessage";
import EditRoomDialogButton from "@/components/components-salles/admin/crud/room/EditRoomDialogButton";
import DeskList from "@/components/components-salles/admin/details/room/DesksList";
import DetailsRoom from "@/components/components-salles/public/room/detail/DetailsRoom.vue";
import PlanCanvas from "@/components/components-salles/common/PlanCanvas";
import PlanAdminConfig from "@/models/plan/AdminConfig.json";
import PlanUserConfig from "@/models/plan/UserConfig.json";
import { mapActions, mapGetters } from "vuex";
import notif from "@/services/common/NotificationHelperService";
import i18n from "@/i18n";
import EditDeskDialogButton from "../../../../components/components-salles/admin/crud/desk/EditDeskDialogButton.vue";
import { DateTime, Interval } from "luxon";

export default {
  name: "RoomDetailsView",
  components: {
    NotificationMessage,
    EditRoomDialogButton,
    DeskList,
    DetailsRoom,
    PlanCanvas,
    EditDeskDialogButton,
  },
  data: () => ({
    planConfigs: [PlanAdminConfig, PlanUserConfig],
    planConfigIndex: 0,
    roomCopy: null,
    newDesk: {},
  }),
  computed: {
    ...mapGetters("roomModule", { roomById: "getRoomById" }),
    ...mapGetters("deskModule", {
      desksByRoomId: "getDesksByRoomId",
      availableDesksByRoom: "getAvailableDesksByRoom",
    }),
    ...mapGetters("bookingModule", ["getRoomDesksBookedOnInterval"]),

    room() {
      return this.roomById(parseInt(this.$route.params.roomId));
    },
    desks() {
      return this.desksByRoomId(this.room.id);
    },
    planConfig() {
      return this.planConfigs[this.planConfigIndex];
    },
    centerTooltip() {
      return this.$t("crudRooms.redirectCenterList");
    },
    roomTooltip() {
      return this.$t("crudRooms.redirectRoomsList");
    },
    deskTooltip() {
      return this.$t("crudRooms.redirectDesksList");
    },
    allDesks() {
      return this.desks.filter((desk) => desk.deskType == "DESK");
    },
    allEquipments() {
      return this.desks.filter((desk) => desk.deskType == "EQUIPMENT");
    },
    availableDesks() {
      return this.availableDesksByRoom(this.room);
    },
    bookInterval() {
      const day = new DateTime(DateTime.now());
      const interval = new Interval.fromDateTimes(
        DateTime.local(day.year, day.month, day.day, 9),
        DateTime.local(day.year, day.month, day.day, 18)
      );
      return interval;
    },
    bookedDesks() {
      if (!this.bookInterval) return undefined;
      return this.getRoomDesksBookedOnInterval(
        this.room,
        this.availableDesks,
        this.bookInterval
      );
    },
    getDesksAvailable() {
      const getDesks = this.desks.filter((desk) => desk.deskType == "DESK");
      this.desksAvailable = getDesks.length - this.bookedDesks.desks.size;
      return this.desksAvailable;
    },
  },
  methods: {
    ...mapActions("deskModule", [
      "loadDesksWithDependents",
      "editDesk",
      "showDeskStoreNotifications",
    ]),
    ...mapActions("deskSlotModule", ["loadDeskSlotsWithDependents"]),
    ...mapActions("userModule", ["loadUsersWithDependents"]),
    ...mapActions("subscriptionModule", [
      "subscribeToResource",
      "unsubscribeFromResource",
      "getSubscribedResources",
    ]),

    clearNewDesk() {
      this.newDesk = {};
    },
    changeStyle() {
      ++this.planConfigIndex;
      if (this.planConfigIndex >= this.planConfigs.length)
        this.planConfigIndex = 0;
      this.$refs.desksPlan.drawImage = this.planConfigIndex == 0;
      this.$refs.desksPlan.requestCanvasFrame();
    },
    async saveDesks() {
      try {
        this.showDeskStoreNotifications(false);
        try {
          for (let desk of this.desks) await this.editDesk(desk);
          notif.showSuccess(
            this.$store.dispatch,
            i18n.t("crudDesks.planSaveAllSuccess")
          );
        } catch (e) {
          notif.showError(
            this.$store.dispatch,
            i18n.t("crudDesks.planSaveAllError")
          );
        }
      } finally {
        this.showDeskStoreNotifications(true);
      }
    },
    resizeDesks() {
      for (let desk of this.desks) {
        if (desk.deskType == "DESK") {
          desk.x = 0;
          desk.y = 0;
          desk.w = 200;
          desk.h = 200;
        } else {
          desk.x = 0;
          desk.y = 0;
          desk.w = 300;
          desk.h = 100;
        }
      }
      this.planRef.requestCanvasFrame();
    },

    resizeDesk(desk) {
      desk.x = 0;
      desk.y = 0;
      desk.w = 100;
      desk.h = 100;
      this.planRef.requestCanvasFrame();
    },
    copyRoom() {
      this.roomCopy = structuredClone(this.room);
    },
    returnToRoomList(item) {
      this.$router.push({
        name: "admin-center-rooms",
        params: {
          centerId: item.center.id,
        },
      });
    },
    redirectToCenters(item) {
      this.$router.push({
        name: "admin-center-details",
        params: {
          centerId: item.centerId,
        },
      });
    },
    async subscription(item) {
      const resources = await this.getSubscribedResources();
      if (resources.includes(item)) {
        return this.unsubscribeFromResource(item);
      } else {
        return this.subscribeToResource(item);
      }
    },
  },
  async mounted() {
    await this.loadUsersWithDependents();
    await this.loadDesksWithDependents();
    await this.loadDeskSlotsWithDependents();
  },
};
</script>

<style lang="scss" scoped>
.list {
  border-radius: 10px;
}
.notification {
  position: absolute;
  padding: 0 1.5rem;
  z-index: 100;
}

.layout {
  margin: 0 1rem;
  display: flex;
  justify-content: center;
}
.parent {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(6, 1fr);
  grid-column-gap: 0px;
  grid-row-gap: 0px;
}
.div1 {
  grid-area: 1 / 1 / 2 / 6;
  margin: 0;
  padding: 0;
}
.div2 {
  grid-area: 2 / 1 / 7 / 3;
  margin: 0;
}

.div3 {
  grid-area: 2 / 3 / 7 / 6;
  margin-top: 0;
}

.rooms {
  display: flex;
  flex-direction: row;
}
.btn-position {
  display: flex;
  justify-content: end;
  margin-top: 5px;
}
.desks-list {
  user-select: none;
  min-width: 300px;
}
.space-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.plan-desks {
  overflow: auto;
}
h3 {
  align-items: center;
  display: flex;
  justify-content: center;
}
</style>
